import { ChangeDetectorRef, Directive, OnDestroy, Pipe, PipeTransform, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { EnumLabelService } from './services/enum-label/enum-label.service';

/**
 * Base for impure label pipes that follow the active language: re-renders OnPush hosts on
 * language change and memoizes on (args, language) since impure pipes run every change detection.
 */
@Directive()
export abstract class LanguageAwareLabelPipe implements OnDestroy {
  protected readonly enumLabels = inject(EnumLabelService);
  private readonly transloco = inject(TranslocoService);
  private readonly langSub: Subscription;
  private lastArgs: unknown[] | null = null;
  private lastLang: string | null = null;
  private lastResult = '';

  constructor() {
    const cdr = inject(ChangeDetectorRef);
    this.langSub = this.transloco.langChanges$.subscribe(() => cdr.markForCheck());
  }

  protected memoize(args: unknown[], compute: () => string): string {
    const lang = this.transloco.getActiveLang();
    if (this.lastArgs && lang === this.lastLang && args.length === this.lastArgs.length && args.every((a, i) => a === this.lastArgs![i])) {
      return this.lastResult;
    }
    this.lastArgs = args;
    this.lastLang = lang;
    this.lastResult = compute();
    return this.lastResult;
  }

  ngOnDestroy(): void {
    this.langSub.unsubscribe();
  }
}

/**
 * Displays an enum value in the active language.
 * Usage: {{ order.orderStatusTypeID | enumLabel:'orderStatusType' }}
 *        {{ order.orderTypeID | enumLabel:'orderType':order.orderType?.name }}   (API name as fallback)
 */
@Pipe({
  name: 'enumLabel',
  pure: false,
  standalone: false
})
export class EnumLabelPipe extends LanguageAwareLabelPipe implements PipeTransform {
  transform(value: string | number | boolean | null | undefined, enumName: string, fallbackName?: string | null): string {
    return this.memoize([value, enumName, fallbackName], () => this.enumLabels.label(enumName, value, fallbackName));
  }
}

/**
 * Displays a jurisdiction code in the active language (jurisdictions.<country>.<code>).
 * Usage: {{ order.jurisdiction | jurisdictionLabel:order.country:order.displayJurisdiction }}
 *        {{ j.jurisdictionCode | jurisdictionLabel:'CA':j.jurisdictionFullName }}
 */
@Pipe({
  name: 'jurisdictionLabel',
  pure: false,
  standalone: false
})
export class JurisdictionLabelPipe extends LanguageAwareLabelPipe implements PipeTransform {
  transform(code: string | null | undefined, country?: string | null, fallbackName?: string | null): string {
    return this.memoize([code, country, fallbackName], () => this.enumLabels.jurisdiction(code, country, fallbackName));
  }
}

/**
 * Displays English text sent by the API (UI configuration titles) in the active language (uiConfigurationText.<Text>).
 * Usage: {{ uiConfiguration.collateralValueTitle | uiText }}
 */
@Pipe({
  name: 'uiText',
  pure: false,
  standalone: false
})
export class UiTextPipe extends LanguageAwareLabelPipe implements PipeTransform {
  transform(text: string | null | undefined): string {
    return this.memoize([text], () => this.enumLabels.apiText(text));
  }
}

/**
 * Displays a country code in the active language (countries.<code>).
 * Usage: {{ country.countryCode | countryLabel:country.countryFullName }}
 */
@Pipe({
  name: 'countryLabel',
  pure: false,
  standalone: false
})
export class CountryLabelPipe extends LanguageAwareLabelPipe implements PipeTransform {
  transform(code: string | null | undefined, fallbackName?: string | null): string {
    return this.memoize([code, fallbackName], () => this.enumLabels.country(code, fallbackName));
  }
}
